import { Trans, useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useRef, useState } from "react";
import { SUBTITLE_EXTENSIONS } from "@shared/ingest-phases";
import { narrowAssetLanguage } from "@shared/vod-title";
import { foyerApi, ownerIngestApi, type IngestJobView } from "../../http/foyer";
import { uploadInBlocks } from "../../lib/blockUpload";
import { describeError, useToast } from "../toast";
import { tokens } from "../../theme";
import { translateKey, type Translate } from "../../i18n/translate";

/**
 * Textning — the subtitle files attached to an ingest (`BE-32` over
 * `CONTENT-21`).
 *
 * ## ⚠️ What this tab is NOT, said on the tab itself
 *
 * It is not "the subtitles of this title", because the catalogue has no such
 * concept. `Media.subtitleLanguages[]` upstream is a list of **labels** on a
 * media that does not exist until a transcode has produced one, and there is no
 * subtitle file store in the Content API at all — the portal ships its files to
 * a separate Azure Function whose URL carries its own key inline in committed
 * source (`OLOF-7`, never to be copied into Foyer).
 *
 * So what a file uploaded here does is ride ALONGSIDE the content to delivery.
 * The tab says that out loud rather than letting somebody infer that uploading
 * a `.vtt` makes a language appear on the title. The alternative the ticket
 * offered — defer the tab entirely — was rejected because the upload path
 * already exists and works; what was missing was only a way to reach it.
 *
 * ## Why it is bound to a JOB and not to the title
 *
 * A subtitle is an input to a transcode, so it attaches to the ingest that is
 * about to run and is refused once that ingest has left `queued`: the
 * transcoder already has its input list, and a file added afterwards would be a
 * language the title claims and the rendition does not carry. With no queued
 * job there is nothing to attach to, and this tab says so instead of offering a
 * button that 409s.
 */

/** What a studio actually ships, in the order they ship it. */
const COMMON_LANGUAGES = [
  { code: "sv", label: "Svenska" },
  { code: "en", label: "English" },
  { code: "no", label: "Norsk" },
  { code: "da", label: "Dansk" },
  { code: "fi", label: "Suomi" },
  { code: "de", label: "Deutsch" },
  { code: "fr", label: "Français" },
  { code: "es", label: "Español" },
];

function bytesLabel(t: Translate, bytes: number | null): string {
  const k = (name: string, count?: number) =>
    translateKey(t, "content:subtitlesTab.bytes." + name, { count });
  if (bytes === null) return k("notUploaded");
  if (bytes === 0) return k("empty");
  if (bytes < 1024) return k("b", bytes);
  return k("kb", Math.round(bytes / 1024));
}

export default function SubtitlesTab({
  jobs,
  onChanged,
  surface = "staff",
}: {
  jobs: IngestJobView[] | null;
  onChanged: () => void | Promise<void>;
  /** A supplier's dialog must pass `owner`: the staff routes need administrate on titles at Nenda. */
  surface?: "staff" | "owner";
}) {
  const toast = useToast();
  const { t } = useTranslation(["content", "common"]);
  const api = surface === "owner" ? ownerIngestApi : foyerApi;
  const [language, setLanguage] = useState("sv");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  // The job a subtitle can still be attached to. Deliberately `queued` only.
  const queued = jobs?.find((j) => j.phase === "queued") ?? null;
  const latest = jobs?.[0] ?? null;
  const attached = latest?.subtitles ?? [];

  async function upload(file: File) {
    if (!queued) return;
    setError(null);
    const dot = file.name.lastIndexOf(".");
    const extension = dot > 0 ? file.name.slice(dot + 1).toLowerCase() : "";
    if (!SUBTITLE_EXTENSIONS.includes(extension)) {
      setError(
        t("subtitlesTab.onlyExtensions", {
          extensions: SUBTITLE_EXTENSIONS.join(", "),
        })
      );
      return;
    }

    setBusy(t("subtitlesTab.busy.askingStorage"));
    try {
      const target = await api.ingestSubtitleUrl(queued.id, {
        language,
        extension,
      });
      setBusy(t("subtitlesTab.busy.uploading", { name: file.name }));
      await uploadInBlocks(target.uploadUrl, file, {
        contentType: extension === "vtt" ? "text/vtt" : "application/x-subrip",
      });
      setBusy(t("subtitlesTab.busy.verifying"));
      await api.ingestSubtitleUploaded(queued.id, target.language);
      if (target.narrowedFrom) {
        // Said out loud: `pt-BR` and `pt` are different files to whoever made
        // them, and a supplier not told will send both.
        setError(
          t("subtitlesTab.narrowed", {
            language: target.language,
            from: target.narrowedFrom,
          })
        );
      }
      toast.success(
        t("subtitlesTab.uploaded", {
          name: file.name,
          language: target.language,
        })
      );
      await onChanged();
    } catch (e) {
      const err = e as { code?: string; message: string };
      toast.error(
        describeError(t, "uploadSubtitleFile", e, { name: file.name })
      );
      setError(
        err.code === "storage_not_configured" ||
          err.code === "ingest_already_started" ||
          err.code === "no_bytes_uploaded"
          ? t(`subtitlesTab.errors.${err.code}`)
          : err.message
      );
    } finally {
      setBusy(null);
    }
  }

  const narrowed = narrowAssetLanguage(language);

  return (
    <Box data-testid="subtitles-tab">
      <Alert severity="info" sx={{ mb: 2, typography: "meta" }}>
        <Trans t={t} i18nKey="subtitlesTab.info" components={{ b: <b /> }} />
      </Alert>

      {error && (
        <Alert severity="warning" sx={{ mb: 2, typography: "meta" }}>
          {error}
        </Alert>
      )}

      {attached.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="meta"
            sx={{ fontWeight: "fontWeightBold", mb: 0.75 }}
          >
            {t("subtitlesTab.attachedHeading")}
          </Typography>
          <Stack spacing={0.75}>
            {attached.map((s) => (
              <Stack
                key={s.language}
                direction="row"
                spacing={1}
                alignItems="center"
                data-testid={`subtitle-${s.language}`}
              >
                <Chip size="small" variant="outlined" label={s.language} />
                <Typography
                  variant="micro"
                  sx={{ color: s.bytes ? tokens.inkMuted : tokens.warning }}
                >
                  {bytesLabel(t, s.bytes)}
                  {s.narrowedFrom
                    ? t("subtitlesTab.narrowedFrom", { from: s.narrowedFrom })
                    : ""}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}

      {queued === null ? (
        <Typography
          variant="meta"
          sx={{ color: tokens.inkMuted }}
          data-testid="subtitles-no-queued-job"
        >
          {jobs === null
            ? t("common:states.loading")
            : jobs.length === 0
              ? t("subtitlesTab.startIngestFirst")
              : t("subtitlesTab.leftQueue")}
        </Typography>
      ) : (
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <TextField
            select
            size="small"
            label={t("common:labels.language")}
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            sx={{ minWidth: 180 }}
            helperText={
              narrowed?.narrowedFrom
                ? t("subtitlesTab.filedAs", { code: narrowed.code })
                : t("subtitlesTab.codeHint")
            }
            inputProps={{ "data-testid": "subtitle-language" }}
          >
            {COMMON_LANGUAGES.map((l) => (
              <MenuItem key={l.code} value={l.code}>
                {l.label} ({l.code})
              </MenuItem>
            ))}
          </TextField>

          <input
            ref={fileInput}
            type="file"
            hidden
            accept=".srt,.vtt"
            data-testid="subtitle-file"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) void upload(file);
            }}
          />
          <Button
            variant="contained"
            size="small"
            sx={{ mt: 0.5 }}
            disabled={Boolean(busy)}
            onClick={() => fileInput.current?.click()}
            data-testid="subtitle-upload"
          >
            {t("subtitlesTab.addFile")}
          </Button>
        </Stack>
      )}

      {busy && (
        <Box sx={{ mt: 1.5 }}>
          <LinearProgress sx={{ borderRadius: 1 }} />
          <Typography variant="micro" sx={{ color: tokens.inkMuted, mt: 0.5 }}>
            {busy}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
